/**
 * RawIframe — headerless iframe component for embedding tools.
 *
 * Just the iframe + loading overlay + error state.
 * No header, no "Open in new tab" button, no artifact type detection.
 */

import React, { useState, useCallback } from 'react';

interface RawIframeProps {
  url: string;
  title: string;
  onLoad?: () => void;
  onError?: (error: string) => void;
  className?: string;
  /** Google Workspace email the user should be logged into to view embedded sheets */
  googleWorkspaceEmail?: string | null;
}

export default function RawIframe({ url, title, onLoad, onError, className, googleWorkspaceEmail }: RawIframeProps) {
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);

  const handleLoad = useCallback(() => {
    setIsLoading(false);
    onLoad?.();
  }, [onLoad]);

  const handleError = useCallback(() => {
    setIsLoading(false);
    setHasError(true);
    onError?.('Failed to load embedded content');
  }, [onError]);

  if (hasError) {
    return (
      <div className={`flex items-center justify-center bg-bg-warm ${className || 'h-full'}`}>
        <div className="text-center max-w-sm px-4">
          {googleWorkspaceEmail ? (
            <>
              <p className="text-sm text-fg-secondary mb-3">
                To view the underlying workbook, please log into your organisation's G Suite account with BARK (<span className="font-semibold">{googleWorkspaceEmail}</span>) via{' '}
                <a
                  href="https://accounts.google.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-accent hover:text-accent-hover underline"
                >
                  this link
                </a>.
              </p>
              <button
                onClick={() => { setHasError(false); setIsLoading(true); }}
                className="text-xs text-fg-tertiary hover:text-fg-secondary underline"
              >
                Retry loading
              </button>
            </>
          ) : (
            <>
              <p className="text-sm text-danger mb-2">Failed to load content</p>
              <a
                href={url}
                target="_blank"
                rel="noopener noreferrer"
                className="text-xs text-accent hover:text-accent-hover underline"
              >
                Open in new tab
              </a>
            </>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className={`relative ${className || 'h-full'}`}>
      {/* Loading overlay */}
      {isLoading && (
        <div className="absolute inset-0 flex items-center justify-center bg-surface/80 z-10">
          <div className="text-center">
            <div className="w-6 h-6 border-2 border-accent border-t-transparent rounded-full animate-spin mx-auto mb-2" />
            <p className="text-xs text-fg-tertiary">Loading...</p>
          </div>
        </div>
      )}

      <iframe
        src={url}
        title={title}
        onLoad={handleLoad}
        onError={handleError}
        className="w-full h-full border-0"
        sandbox="allow-scripts allow-same-origin allow-popups allow-forms"
      />
    </div>
  );
}
